import SnackbarAlert, { AlertMessage } from '@/features/shared/SnackbarAlert';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type AlertSeverities = 'info' | 'error' | 'warning' | 'success';

type AlertTrigger = (message: AlertMessage) => void;

const MessageContext = createContext<{
  info: AlertTrigger;
  error: AlertTrigger;
  success: AlertTrigger;
  warning: AlertTrigger;
} | null>(null);

export const MessageProvider = ({ children }: PropsWithChildren<any>) => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({ message: '' });
  const [messageSeverity, setMessageSeverity] = useState<AlertSeverities>('info');

  const generateMessage = useCallback(
    (severity: AlertSeverities) => (message: AlertMessage) => {
      setMessageSeverity(severity);
      setAlertMessage(message);
      setShowAlert(true);
    },
    [setMessageSeverity, setAlertMessage, setShowAlert],
  );

  const valueContext = useMemo(
    () => ({
      error: generateMessage('error'),
      info: generateMessage('info'),
      success: generateMessage('success'),
      warning: generateMessage('warning'),
    }),
    [generateMessage],
  );

  return (
    <MessageContext.Provider value={valueContext}>
      <>
        {children}
        <SnackbarAlert
          severity={messageSeverity}
          onClose={() => setShowAlert(false)}
          open={showAlert}
          alertMessage={alertMessage}
        />
      </>
    </MessageContext.Provider>
  );
};

export const useMessage = () => {
  const context = useContext(MessageContext);

  if (!context) {
    throw new Error('You need the MessageProvider');
  }
  return context;
};
